import { createContext, useContext, type JSX, type ReactNode } from 'react';

import type { Wizard, WizardImplementations } from '../wizard/types';
import { useWizard, type UseWizardResult } from './use-wizard';

export type WizardScopeProps<TStep extends string> = WizardImplementations<TStep> & {
  children: ReactNode;
};

/**
 * Share one wizard actor across a component tree. `useWizard` creates a
 * private actor per call site — right for a flow owned by one provider, wrong
 * when many sibling/descendant components need the same stepper. The scope
 * puts a single `useWizard` behind context:
 *
 * ```tsx
 * const { WizardScope: ObjectiveScope, useWizardScope: useObjectiveWizard } =
 *   createWizardScope(objectiveWizard);
 *
 * <ObjectiveScope guards={guards} input={{ step: initialStep }}>
 *   <Sidebar />   // const w = useObjectiveWizard();
 *   <StepBody />  // same actor, same snapshot
 * </ObjectiveScope>
 * ```
 *
 * Impls (`guards` / `onTransition` / `persistence` / `input`) are props on the
 * scope component, with `useWizard`'s semantics: read fresh through a ref,
 * except `persistence` presence and `input`, which are fixed at mount.
 */
export function createWizardScope<TStep extends string>(
  wizard: Wizard<TStep>
): {
  WizardScope: (props: WizardScopeProps<TStep>) => JSX.Element;
  useWizardScope: () => UseWizardResult<TStep>;
} {
  const ScopeContext = createContext<UseWizardResult<TStep> | null>(null);

  function WizardScope({ children, ...impls }: WizardScopeProps<TStep>): JSX.Element {
    const state = useWizard(wizard, impls);
    return <ScopeContext.Provider value={state}>{children}</ScopeContext.Provider>;
  }

  function useWizardScope(): UseWizardResult<TStep> {
    const value = useContext(ScopeContext);
    if (!value) {
      throw new Error(
        `[@heidi/flow] useWizardScope for "${wizard.id}" was called outside its WizardScope.`
      );
    }
    return value;
  }

  return { WizardScope, useWizardScope };
}
